import React, { useState, useEffect, useRef } from 'react'
import {
  Package,
  DollarSign,
  BarChart3
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import { toast } from 'react-toastify'
import assetAcquisitionService from '../../../services/assetAcquisitionService'
import { formatDate, formatAmount, statusBadgeClass } from '../assetUtils'
// Consolidated CSS is imported globally

const AssetValuationReport = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const response = await assetAcquisitionService.getAllAcquisitions({ limit: 1000 })
      if (response.success) {
        setData((response.data || []).map(item => ({ ...item, id: item._id })))
      } else {
        toast.error(response.message || 'Failed to load valuation data')
      }
    } catch (error) {
      console.error('Error loading asset valuation report:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load asset valuation report')
      }
    } finally {
      setLoading(false)
    }
  }

  const totalCost = data.reduce((sum, item) => sum + Number(item.purchaseCost || 0), 0)
  const averageCost = data.length > 0 ? totalCost / data.length : 0

  const columns = [
    {
      accessorKey: 'acquisitionCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetName',
      header: 'Asset Name',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'assetType',
      header: 'Asset Type',
      cell: ({ row }) => {
        const assetType = row.original.assetType
        return (
          <div style={{ textAlign: 'left' }}>
            {assetType ? (assetType.assetTypeName || assetType.assetTypeCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'purchaseDate',
      header: 'Purchase Date',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'purchaseCost',
      header: 'Purchase Cost',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatAmount(row.original.purchaseCost, row.original.currency)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${statusBadgeClass(getValue())}`}>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Asset Valuation', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Valuation Report</h1>
          <p>Asset value and depreciation analysis</p>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Package size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Count</span>
            </div>
          </div>
          <div className="metric-value">{data.length}</div>
          <div className="metric-title">Total Assets</div>
          <div className="metric-subtitle">Acquired assets</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(totalCost)}</div>
          <div className="metric-title">Total Asset Value</div>
          <div className="metric-subtitle">Purchase cost</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <BarChart3 size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Average</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(averageCost)}</div>
          <div className="metric-title">Average Cost</div>
          <div className="metric-subtitle">Per asset</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search assets..."
          noCard={true}
          pageSize={10}
        />
      </div>
    </div>
  )
}

export default AssetValuationReport
